import "./styles/nav.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../images/Round-logo.webp";
import React, { useEffect, useState } from "react";

function ArrowIcon() {
  return (
    <svg className="navArrow" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg className="navMenuIcon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="navCloseIcon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 5l14 14M19 5L5 19" />
    </svg>
  );
}

export default function Nav() {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenu, setMobileMenu] = useState(false);

  const closeMenu = () => {
    setMobileMenu(false);
  };

  const scrollToSection = (section) => {
    const element = document.getElementById(section);

    if (!element) return false;

    window.scrollTo({
      top: element.getBoundingClientRect().top + window.scrollY,
      behavior: "smooth",
    });

    return true;
  };

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/" || !location.hash) return undefined;

    let section;
    try {
      section = decodeURIComponent(location.hash.slice(1));
    } catch {
      return undefined;
    }

    let frameId;
    let resizeTimer;
    let attempts = 0;
    let stopped = false;
    let observer;

    const alignSection = (behavior) => {
      const element = document.getElementById(section);
      if (!element || stopped) return false;
      window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY,
        behavior,
      });
      return true;
    };

    // Loading promotions, images and fonts can move the anchor after navigation.
    const realignAfterLayout = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => alignSection("smooth"), 120);
    };

    const stopFollowing = () => {
      stopped = true;
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(resizeTimer);
      observer?.disconnect();
      window.removeEventListener("wheel", stopFollowing);
      window.removeEventListener("touchstart", stopFollowing);
      window.removeEventListener("pointerdown", stopFollowing);
      window.removeEventListener("keydown", onKeyDown);
    };

    const onKeyDown = (event) => {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Tab"].includes(event.key)) {
        stopFollowing();
      }
    };

    const scrollToHash = () => {
      if (stopped) return;
      if (alignSection("smooth")) {
        const home = document.querySelector(".Home");
        if (home && typeof ResizeObserver !== "undefined") {
          observer = new ResizeObserver(realignAfterLayout);
          observer.observe(home);
        }
        return;
      }
      if (attempts >= 60) return;
      attempts += 1;
      frameId = window.requestAnimationFrame(scrollToHash);
    };

    window.addEventListener("wheel", stopFollowing, { passive: true });
    window.addEventListener("touchstart", stopFollowing, { passive: true });
    window.addEventListener("pointerdown", stopFollowing, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    frameId = window.requestAnimationFrame(scrollToHash);

    return stopFollowing;
  }, [location.hash, location.pathname]);

  useEffect(() => {
    document.body.classList.toggle("nav-menu-open", mobileMenu);

    return () => {
      document.body.classList.remove("nav-menu-open");
    };
  }, [mobileMenu]);

  const goHome = () => {
    closeMenu();

    if (location.pathname === "/") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      navigate("/");
    }
  };

  const goToSection = (section) => {
    closeMenu();

    if (location.pathname === "/") {
      scrollToSection(section);
    } else {
      navigate(`/#${section}`);
    }
  };

  return (
    <>
      <nav className={`siteNav ${mobileMenu ? "siteNav--open" : ""}`}>
        <div className="navPill">
          {/* ================================
              TUBBEES LOGO
          ================================= */}

          <button
            type="button"
            className="navLogo"
            onClick={goHome}
            aria-label="Tubbees home"
          >
            <img src={logo} alt="Tubbees" />
          </button>

          {/* ================================
              DESKTOP NAVIGATION
          ================================= */}

          <div className="navLinks">
            <Link to="/" className="navLink">
              Home
            </Link>

            <Link to="/menu" className="navLink">
              Menu
            </Link>

            <Link to="/find-us" className="navLink">
              Find Us
            </Link>

            <a
              href="https://shop.tubbees.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="navLink"
            >
              Webstore
              <ArrowIcon />
            </a>

            <a
              href="https://tubbees.deliverectdirect.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="navLink"
            >
              Click &amp; Collect
              <ArrowIcon />
            </a>

            <Link to="/apply" className="navLink">
              Jobs
            </Link>

            <Link to="/partner" className="navLink">
              Partner
            </Link>

            <button
              type="button"
              className="navLink"
              onClick={() => goToSection("story")}
            >
              About
            </button>
          </div>

          {/* ================================
              SMALL BRAND DETAIL
          ================================= */}

          <div className="navAccent" aria-hidden="true" />

          {/* ================================
              MOBILE MENU
          ================================= */}

          <button
            type="button"
            className="navMobileButton"
            onClick={() => setMobileMenu((open) => !open)}
            aria-label={mobileMenu ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileMenu}
          >
            {mobileMenu ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>

        {/* ================================
            MOBILE NAVIGATION
        ================================= */}

        <div className={`mobileNav ${mobileMenu ? "mobileNav--open" : ""}`}>
          <div className="mobileNavInner">
            <div className="mobileNavLabel">Explore Tubbees</div>

            <button
              type="button"
              className="mobileNavLink"
              onClick={() => goToSection("slush")}
            >
              <span>Slushees</span>
              <ArrowIcon />
            </button>

            <Link to="/menu" className="mobileNavLink" onClick={closeMenu}>
              <span>Menu</span>
              <ArrowIcon />
            </Link>

            <Link to="/find-us" className="mobileNavLink" onClick={closeMenu}>
              <span>Find Us</span>
              <ArrowIcon />
            </Link>

            <a
              href="https://shop.tubbees.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="mobileNavLink"
              onClick={closeMenu}
            >
              <span>Webstore</span>
              <ArrowIcon />
            </a>

            <a
              href="https://tubbees.deliverectdirect.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="mobileNavLink"
              onClick={closeMenu}
            >
              <span>Click &amp; Collect</span>
              <ArrowIcon />
            </a>

            <Link to="/apply" className="mobileNavLink" onClick={closeMenu}>
              <span>Jobs</span>
              <ArrowIcon />
            </Link>

            <Link to="/partner" className="mobileNavLink" onClick={closeMenu}>
              <span>Partner</span>
              <ArrowIcon />
            </Link>

            <button
              type="button"
              className="mobileNavLink"
              onClick={() => goToSection("story")}
            >
              <span>About</span>
              <ArrowIcon />
            </button>
          </div>
        </div>
      </nav>
      <button
        type="button"
        className="tubbeesBackTop"
        id="tubbees-back-top"
        aria-label="Scroll to top"
        onClick={() =>
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          })
        }
      >
        <svg
          className="tubbeesBackTopIcon"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 19V5" />
          <path d="M6 11l6-6 6 6" />
        </svg>
      </button>
      {/*
        =====================================================
        PDF LINKS — KEPT FOR FUTURE USE
        =====================================================

        const MENU_PDF = "...";
        const COFFEE_PDF = "...";

        These are intentionally NOT displayed in the navbar.
        =====================================================
      */}
    </>
  );
}
